import React from 'react';
import { BuildingSummary } from '../../../types/protocols';
import { getCoverPhotoStoragePath } from '../../../Helpers/utils';
import BuildingGroupCard from '../../Projects/components/BuildingGroupCard';
import { groupByBuildingName } from '../utils/buildingGrouping';

interface BuildingGroupListProps<T> {
    /** Unique per list; group ids are `${listId}:${buildingKey}` */
    listId: string;
    /** The list's cards in their usual order */
    items: T[];
    getBuildingKey: (item: T) => string;
    getBuildingName: (item: T) => string | undefined;
    renderItem: (item: T) => React.ReactNode;
    /** Toggle text, "<itemLabel> (n)" */
    itemLabel: string;
    itemIcon: React.ReactNode;
    buildingsByKey: Map<string, BuildingSummary>;
    isExpanded: (groupId: string) => boolean;
    onToggle: (groupId: string) => void;
}

/**
 * A Förvaltning list sorted by building: one BuildingGroupCard per building,
 * alphabetically, with the building's cards in the list's order under it.
 */
function BuildingGroupList<T>({
    listId,
    items,
    getBuildingKey,
    getBuildingName,
    renderItem,
    itemLabel,
    itemIcon,
    buildingsByKey,
    isExpanded,
    onToggle
}: BuildingGroupListProps<T>) {
    return (
        <>
            {groupByBuildingName(items, getBuildingKey, getBuildingName).map(group => {
                const groupId = `${listId}:${group.buildingKey}`;
                const building = buildingsByKey.get(group.buildingKey);
                return (
                    <BuildingGroupCard
                        key={groupId}
                        groupId={groupId.replace(':', '-')}
                        buildingName={group.buildingName ?? building?.name}
                        buildingType={building?.buildingType}
                        coverPhotoPath={building ? getCoverPhotoStoragePath(building) : undefined}
                        groupTags={building?.groupTags}
                        itemCount={group.items.length}
                        itemLabel={itemLabel}
                        itemIcon={itemIcon}
                        expanded={isExpanded(groupId)}
                        onToggle={() => onToggle(groupId)}
                    >
                        {group.items.map(renderItem)}
                    </BuildingGroupCard>
                );
            })}
        </>
    );
}

export default BuildingGroupList;
